import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Check, BarChart } from '../components/Icons.jsx';

const questions = [
  {
    id: 'settings', axis: 'exposure',
    q: 'How many distinct clinical settings have you experienced?',
    sub: 'GP, hospital, hospice, mental health — each counts once.',
    options: [
      { label: 'None yet', emoji: '🌱', score: 0 },
      { label: '1 setting', emoji: '🩺', score: 1 },
      { label: '2–3 settings', emoji: '🏥', score: 3 },
      { label: '4+ settings', emoji: '🚑', score: 4 }
    ]
  },
  {
    id: 'inperson', axis: 'exposure',
    q: 'In-person, hands-near-patient?',
    sub: 'Virtual sessions don\'t count here. We mean physically present.',
    options: [
      { label: 'Fully virtual so far', emoji: '💻', score: 0 },
      { label: 'Once or twice', emoji: '👀', score: 2 },
      { label: 'Multiple in-person', emoji: '👩‍⚕️', score: 4 }
    ]
  },
  {
    id: 'patients', axis: 'breadth',
    q: 'Time with vulnerable patient groups?',
    sub: 'Paediatrics, elderly, palliative, mental health.',
    options: [
      { label: 'None', emoji: '⛔', score: 0 },
      { label: 'One group, briefly', emoji: '🌿', score: 2 },
      { label: 'Multiple groups, deeply', emoji: '💚', score: 4 }
    ]
  },
  {
    id: 'specialties', axis: 'breadth',
    q: 'Specialties observed?',
    sub: 'A&E, surgery, GP, psychiatry, paeds — count rotations not lectures.',
    options: [
      { label: '0–1', emoji: '📌', score: 0 },
      { label: '2–3', emoji: '🔬', score: 2 },
      { label: '4+ (incl. surgical)', emoji: '🩻', score: 4 }
    ]
  },
  {
    id: 'reflect_written', axis: 'reflection',
    q: 'Have you written reflections you can quote in a PS?',
    sub: 'Memory doesn\'t survive interview pressure. Written reflections do.',
    options: [
      { label: 'No — mostly memory', emoji: '🤷', score: 0 },
      { label: 'A few bullet points', emoji: '📝', score: 2 },
      { label: 'Structured, GMC-mapped', emoji: '📚', score: 4 }
    ]
  },
  {
    id: 'reflect_what', axis: 'reflection',
    q: 'Can you tell the story of one moment that changed you?',
    sub: 'Specific moment, not a summary of the week.',
    options: [
      { label: 'Not really', emoji: '😶', score: 0 },
      { label: 'Maybe, broadly', emoji: '🤔', score: 2 },
      { label: 'Yes, in detail', emoji: '💡', score: 4 }
    ]
  },
  {
    id: 'duration', axis: 'longitudinal',
    q: 'Longest single placement / commitment?',
    sub: 'One-off shadows don\'t compound. Regular contact does.',
    options: [
      { label: 'A day or two', emoji: '⚡', score: 0 },
      { label: 'A week-long block', emoji: '📆', score: 2 },
      { label: '6+ weeks regular', emoji: '🎯', score: 4 }
    ]
  },
  {
    id: 'recent', axis: 'longitudinal',
    q: 'Anything ongoing right now?',
    sub: 'Admissions tutors love a present-tense answer here.',
    options: [
      { label: 'No — all past', emoji: '🕰️', score: 0 },
      { label: 'Yes — regular thing', emoji: '🔥', score: 4 }
    ]
  }
];

const axes = {
  exposure: { label: 'Clinical exposure', icon: '🏥', max: 8 },
  breadth: { label: 'Breadth of cases', icon: '🌐', max: 8 },
  reflection: { label: 'Reflection depth', icon: '✍️', max: 8 },
  longitudinal: { label: 'Longitudinal commitment', icon: '📈', max: 8 }
};

const fixesByAxis = {
  exposure: 'A 5-day in-person Career Camp (London / Birmingham / Manchester) closes this fast. Virtual alone tops out around 4/8.',
  breadth: 'Career Camp rotates you through A&E, surgery, GP, mental health and hospice in one week — exactly the breadth axis.',
  reflection: 'Reflections need a framework. Use the Reflection generator (in Tools) to convert raw notes into PS-ready paragraphs.',
  longitudinal: 'Six months of regular hospice or care-home contact compounds. Pair it with a Career Camp week for depth + duration.'
};

export default function WeQuiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const done = step >= questions.length;

  const scores = useMemo(() => {
    const s = { exposure: 0, breadth: 0, reflection: 0, longitudinal: 0 };
    for (const q of questions) {
      const picked = answers[q.id];
      if (picked != null) s[q.axis] += picked;
    }
    return s;
  }, [answers]);

  const total = scores.exposure + scores.breadth + scores.reflection + scores.longitudinal;
  const totalMax = 32;
  const band = total <= 8 ? 'Starting line' : total <= 16 ? 'In progress' : total <= 24 ? 'Solid' : 'Application-ready';
  const bandColor = total <= 8 ? '#b45309' : total <= 16 ? '#b45309' : total <= 24 ? 'var(--brand-purple)' : 'var(--live-green)';
  const weakest = Object.keys(scores).reduce((w, k) => (scores[k] / axes[k].max < scores[w] / axes[w].max ? k : w), 'exposure');

  const choose = (qId, score) => {
    setAnswers({ ...answers, [qId]: score });
    setTimeout(() => setStep((s) => s + 1), 220);
  };
  const back = () => setStep(Math.max(0, step - 1));
  const restart = () => { setStep(0); setAnswers({}); };

  if (done) {
    return (
      <>
        <div className="page-head">
          <Link to="/work-experience" className="back-link"><ArrowLeft width="16" height="16" />Back to Work experience</Link>
          <div className="kicker" style={{ color: bandColor, textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12, marginTop: 12 }}>Result · {band}</div>
          <h1 style={{ fontSize: 'clamp(40px, 8vw, 56px)', margin: '8px 0 6px', display: 'flex', alignItems: 'baseline', gap: 12 }}>
            {total}<span style={{ color: 'var(--text-muted)', fontSize: 24, fontWeight: 500 }}>/ {totalMax}</span>
          </h1>
        </div>

        <div className="card" style={{ marginBottom: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 18 }}>
            {Object.keys(axes).map((k) => {
              const pct = (scores[k] / axes[k].max) * 100;
              return (
                <div key={k}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 6 }}>
                    <span style={{ fontWeight: 600 }}><span style={{ marginRight: 6 }}>{axes[k].icon}</span>{axes[k].label}</span>
                    <span style={{ color: 'var(--text-muted)' }}>{scores[k]} / {axes[k].max}</span>
                  </div>
                  <div style={{ height: 10, background: 'var(--card-soft)', borderRadius: 99, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', transition: 'width 600ms ease' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card" style={{ marginBottom: 18, borderLeft: '5px solid var(--brand-purple)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Your weakest axis</div>
          <h3 style={{ margin: '6px 0 8px', fontSize: 22 }}>{axes[weakest].icon} {axes[weakest].label}</h3>
          <p style={{ margin: 0, color: 'var(--text-2)' }}>{fixesByAxis[weakest]}</p>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
          <button onClick={restart} className="btn btn-secondary">Re-take quiz</button>
          <Link to="/we-reflection" className="btn btn-secondary">Try Reflection generator <ArrowRight width="14" height="14" /></Link>
          <Link to="/career-camp" className="btn btn-primary">See Career Camp <ArrowRight width="14" height="14" /></Link>
        </div>
      </>
    );
  }

  const q = questions[step];

  return (
    <>
      <div className="page-head" style={{ marginBottom: 14 }}>
        <Link to="/work-experience" className="back-link"><ArrowLeft width="16" height="16" />Back to Work experience</Link>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
        <span style={{ fontWeight: 600 }}>{axes[q.axis].icon} {axes[q.axis].label}</span>
        <span>Question {step + 1} of {questions.length}</span>
      </div>
      <div style={{ height: 6, background: 'var(--card-soft)', borderRadius: 99, overflow: 'hidden', marginBottom: 28 }}>
        <div style={{ width: `${(step / questions.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, var(--brand-purple), var(--brand-teal))', transition: 'width 250ms ease' }} />
      </div>

      <div key={q.id} className="card" style={{ animation: 'fadeUp 280ms ease', padding: '28px 28px 24px' }}>
        <h1 style={{ margin: '0 0 8px', fontSize: 'clamp(20px, 4vw, 28px)', letterSpacing: '-0.01em' }}>{q.q}</h1>
        <p style={{ margin: '0 0 22px', color: 'var(--text-muted)', fontSize: 15 }}>{q.sub}</p>

        <div style={{ display: 'grid', gridTemplateColumns: q.options.length >= 4 ? 'repeat(2, 1fr)' : '1fr', gap: 12 }}>
          {q.options.map((opt) => {
            const isPicked = answers[q.id] === opt.score;
            return (
              <button
                key={opt.label}
                onClick={() => choose(q.id, opt.score)}
                style={{
                  padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left',
                  borderRadius: 14, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                  border: `2px solid ${isPicked ? 'var(--brand-purple)' : 'var(--border)'}`,
                  background: isPicked ? 'var(--accent-soft)' : '#fff',
                  color: 'var(--text)',
                  transform: isPicked ? 'scale(1.02)' : 'scale(1)',
                  transition: 'transform 180ms ease, border-color 180ms ease, background 180ms ease'
                }}
                onMouseEnter={(e) => { if (!isPicked) e.currentTarget.style.borderColor = 'var(--brand-purple)'; }}
                onMouseLeave={(e) => { if (!isPicked) e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                <span style={{ fontSize: 28, lineHeight: 1 }}>{opt.emoji}</span>
                <span style={{ flex: 1 }}>{opt.label}</span>
                {isPicked && <Check width="20" height="20" />}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button onClick={back} disabled={step === 0} style={{
          background: 'none', border: 'none', cursor: step === 0 ? 'not-allowed' : 'pointer',
          color: step === 0 ? 'var(--text-muted)' : 'var(--text-2)', fontSize: 14, fontWeight: 600,
          opacity: step === 0 ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: 6
        }}><ArrowLeft width="14" height="14" />Back</button>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Tap an option — we'll move you along.</span>
      </div>

      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </>
  );
}
